import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sheet, SheetAction } from './mobileShared';
import MobileOverview    from './MobileOverview';
import MobileLive        from './MobileLive';
import MobileSold        from './MobileSold';
import MobileTraded      from './MobileTraded';
import MobileWishlist    from './MobileWishlist';
import MobileInvestments from './MobileInvestments';
import MobileAnalytics   from './MobileAnalytics';
import MobileInventory   from './MobileInventory';
import './Mobile.css';

// The phone shell. It replaces the desktop navbar + tab bar below 768px and
// nothing else: every page underneath reads the same props and the same
// helpers as its desktop twin, so the two can never disagree on a number.
//
// Tab ids are deliberately the desktop ids — the parent keeps one `activeTab`
// for both shells, so rotating a tablet or resizing a window lands you on the
// page you were already looking at.

const ICONS = {
  board: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></>,
  armory: <><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="8" rx="1.5" /><rect x="3" y="13" width="8" height="8" rx="1.5" /><rect x="13" y="13" width="8" height="8" rx="1.5" /></>,
  tickets: <><path d="M3 8a2 2 0 0 0 0 8v3h18v-3a2 2 0 0 1 0-8V5H3z" /><path d="M13 5v2M13 11v2M13 17v2" /></>,
  locker: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M14 3v18M17 11v2" /></>,
  more: <><circle cx="5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="19" cy="12" r="1.4" /></>,
  report: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
  swaps: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 12h10M14 9l3 3-3 3" /></>,
  watch: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="0.6" /></>,
  vault: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="12" cy="12" r="4" /><path d="M12 10v2l1.5 1" /></>,
  gear: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
  spark: <><path d="M12 3l2.2 4.6 4.8.7-3.5 3.4.8 4.9-4.3-2.3-4.3 2.3.8-4.9L5 8.3l4.8-.7z" /></>,
  out: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>,
};

const Icon = ({ name }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name]}
  </svg>
);

// Five destinations in the dock is the most a thumb can hit reliably; the
// remaining four live one tap deeper, behind "More".
const DOCK = [
  { id: 'overview3', label: 'Board',   icon: 'board' },
  { id: 'tracker2',  label: 'Armory',  icon: 'armory' },
  { id: 'sold3',     label: 'Tickets', icon: 'tickets' },
  { id: 'extension', label: 'Locker',  icon: 'locker' },
];

const MORE = [
  { id: 'analytics2',   label: 'Report',     icon: 'report', hint: 'Charts, ROI and month-by-month' },
  { id: 'traded2',      label: 'Swaps',      icon: 'swaps',  hint: 'Trades in and out' },
  { id: 'wishlist2',    label: 'Watchboard', icon: 'watch',  hint: 'Skins you are waiting on' },
  { id: 'investments2', label: 'Vault',      icon: 'vault',  hint: 'Long-term positions' },
];

const TITLES = {
  overview3: 'Board', tracker2: 'Armory', sold3: 'Tickets', extension: 'Locker',
  analytics2: 'Report', traded2: 'Swaps', wishlist2: 'Watchboard', investments2: 'Vault',
};

const KNOWN = new Set(Object.keys(TITLES));

export default function MobileApp(props) {
  const {
    activeTab, onChangeTab, user, steamName, navTheme, onToggleTheme, onSettings, onLogout,
    alertCount = 0, pendingTradeCount = 0, saveStatus,
    rows, settings, livePrices, skinsData, inventoryMap, wishlist,
    onEdit, onAddItem, onBulkAdd, onImportInventory, onExportCSV,
    soldCount = 0, tradedCount = 0, investCount = 0,
  } = props;

  const [menu, setMenu] = useState(false);
  const [more, setMore] = useState(false);
  const zone = navTheme === 'zone';

  // Desktop-only tabs (Overview 5/7, Live 3, the legacy Traded tab…) have no
  // phone twin; land on the Board rather than a blank screen.
  const page = KNOWN.has(activeTab) ? activeTab : 'overview3';

  useEffect(() => { window.scrollTo({ top: 0 }); }, [page]);

  const go = id => { setMore(false); onChangeTab(id); };

  const badges = useMemo(() => ({
    sold3: soldCount, traded2: tradedCount, investments2: investCount,
  }), [soldCount, tradedCount, investCount]);

  const moreActive = MORE.some(m => m.id === page);

  // Profit / loss ink. Classic honours the colours picked in Settings; Zone
  // ignores them the same way the desktop board does, because the palette
  // chosen for a dark background is unreadable on candy paper.
  const pnlVars = {
    '--mb-pos': zone ? '#0d7a43' : (settings?.profitTextColor || '#4ade80'),
    '--mb-neg': zone ? '#c8134b' : (settings?.lossTextColor   || '#f87171'),
  };

  return (
    <div className={`mb-app${zone ? ' zn' : ''}`} style={pnlVars}>
      {/* ── Top bar ─────────────────────────────────────── */}
      <header className="mb-top">
        <div className="mb-top-brand">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
          </svg>
          <h1>{TITLES[page]}</h1>
        </div>
        <div className="mb-top-right">
          {saveStatus === 'saving' && <span className="mb-savedot" title="Saving" />}
          {saveStatus === 'error'  && <span className="mb-savedot err" title="Save failed — retrying" />}
          {alertCount > 0 && <span className="mb-pill alert">{alertCount}</span>}
          {pendingTradeCount > 0 && <span className="mb-pill trade">⏳{pendingTradeCount}</span>}
          <button className="mb-iconbtn" onClick={() => setMenu(true)} aria-label="Menu">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* ── Page ────────────────────────────────────────── */}
      <main className="mb-main">
        <AnimatePresence mode="wait">
          <motion.div
            key={page}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            {page === 'overview3' && (
              <MobileOverview rows={rows} settings={settings} livePrices={livePrices}
                skinsData={skinsData} inventoryMap={inventoryMap} zone={zone} />
            )}
            {page === 'tracker2' && (
              <MobileLive rows={rows || []} settings={settings} livePrices={livePrices}
                skinsData={skinsData} inventoryMap={inventoryMap} onEdit={onEdit}
                onAddItem={onAddItem} onBulkAdd={onBulkAdd}
                onImportInventory={onImportInventory} onExportCSV={onExportCSV} />
            )}
            {page === 'sold3' && (
              <MobileSold rows={rows || []} settings={settings} skinsData={skinsData} onEdit={onEdit} />
            )}
            {page === 'traded2' && (
              <MobileTraded rows={rows || []} settings={settings} skinsData={skinsData} livePrices={livePrices} />
            )}
            {page === 'wishlist2' && (
              <MobileWishlist wishlist={wishlist} livePrices={livePrices} skinsData={skinsData} />
            )}
            {page === 'investments2' && (
              <MobileInvestments rows={rows || []} settings={settings} skinsData={skinsData} livePrices={livePrices}
                onEdit={onEdit} onSell={props.onSellVaultUnit}
                onCreateFolder={props.onCreateFolder}
                onAssignFolder={props.onAssignFolder}
                onReturnToLive={props.onReturnToLive} />
            )}
            {page === 'analytics2' && (
              <MobileAnalytics rows={rows} settings={settings} livePrices={livePrices}
                skinsData={skinsData} zone={zone} />
            )}
            {page === 'extension' && (
              <MobileInventory
                user={user}
                livePrices={livePrices}
                syncedInventory={props.syncedInventory}
                setSyncedInventory={props.setSyncedInventory}
                storedItems={props.storedItems}
                selectedForGroup={props.selectedForGroup}
                setSelectedForGroup={props.setSelectedForGroup}
                groupedPurchases={props.groupedPurchases}
                setGroupedPurchases={props.setGroupedPurchases}
                onOpenGroupModal={props.onOpenGroupModal}
                trackedAssetIds={props.trackedAssetIds}
                onSendToTracker={props.onSendToTracker}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* ── Bottom dock ─────────────────────────────────── */}
      <nav className="mb-dock" aria-label="Main">
        {DOCK.map(t => (
          <button
            key={t.id}
            className={`mb-dock-btn ${page === t.id ? 'on' : ''}`}
            onClick={() => go(t.id)}
            aria-current={page === t.id ? 'page' : undefined}
          >
            <span className="mb-dock-icon">
              <Icon name={t.icon} />
              {badges[t.id] > 0 && <em className="mb-dock-badge">{badges[t.id] > 99 ? '99+' : badges[t.id]}</em>}
            </span>
            <span className="mb-dock-label">{t.label}</span>
            {page === t.id && (
              <motion.span layoutId="mb-dock-pip" className="mb-dock-pip"
                transition={{ type: 'spring', stiffness: 500, damping: 36 }} />
            )}
          </button>
        ))}
        <button className={`mb-dock-btn ${moreActive ? 'on' : ''}`} onClick={() => setMore(true)}>
          <span className="mb-dock-icon">
            <Icon name="more" />
            {(badges.traded2 + badges.investments2) > 0 && <em className="mb-dock-badge dim" />}
          </span>
          <span className="mb-dock-label">{moreActive ? TITLES[page] : 'More'}</span>
          {moreActive && (
            <motion.span layoutId="mb-dock-pip" className="mb-dock-pip"
              transition={{ type: 'spring', stiffness: 500, damping: 36 }} />
          )}
        </button>
      </nav>

      {/* ── More sheet ──────────────────────────────────── */}
      <Sheet open={more} onClose={() => setMore(false)} title="All pages">
        <div className="mb-sheet-actions">
          {MORE.map(m => (
            <button key={m.id} className={`mb-navrow ${page === m.id ? 'on' : ''}`} onClick={() => go(m.id)}>
              <span className="mb-navrow-icon"><Icon name={m.icon} /></span>
              <span className="mb-navrow-text">
                <span className="l">{m.label}{badges[m.id] > 0 ? ` · ${badges[m.id]}` : ''}</span>
                <span className="h">{m.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </Sheet>

      {/* ── Menu sheet ──────────────────────────────────── */}
      {/* Same substitution as the desktop navbar: a Steam-created account's
          username is `steam_<17 digits>`, which is the key, not a name. */}
      <Sheet open={menu} onClose={() => setMenu(false)} title={user ? `Signed in as ${steamName || user}` : 'Menu'}>
        <div className="mb-sheet-actions">
          <SheetAction
            icon={<Icon name="spark" />}
            label={zone ? 'Zone theme' : 'Classic theme'}
            hint={zone ? 'Tap for the classic dark look' : 'Tap for the Profit Zone board'}
            onClick={onToggleTheme}
          />
          <SheetAction
            icon={<Icon name="gear" />}
            label="Settings"
            hint="Categories, colours, marketplace fees"
            onClick={() => { setMenu(false); onSettings?.(); }}
          />
          <SheetAction
            icon={<Icon name="out" />}
            label="Log out"
            tone="danger"
            onClick={() => { setMenu(false); onLogout?.(); }}
          />
        </div>
      </Sheet>
    </div>
  );
}
